import React, { useCallback, useEffect } from 'react';

import ANALYTICS_CATEGORIES from '../../../analytics/categories';
import useAccountChange from '../../../fetchHooks/useAccountChange';
import { useRefreshAvtaleGiroProposals } from '../../../fetchHooks/useAvtalegiro';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import usePaymentAccount from '../../../hooks/usePaymentAccount';
import useTranslation from '../../../hooks/useTranslation';
import { isValidAvtalegiroFromAccount } from '../../../services/accountFilters';
import { SigningContextProvider, useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { EikaModal } from '../../EikaModal';
import { SelectedReceiver } from '../SelectedReceiver';
import CreateAvtaleGiroForm from './CreateAvtaleGiroForm';
import { AvtaleGiroActionType } from './form/actions';
import avtalegiroReducer from './form/reducer';
import { avtalegiroInitialState } from './form/state';
import useFormContext, { AvtalegiroFormContext } from './form/useFormContext';
import useValidate from './form/validate';

interface Props {
    show: boolean;
    setShow: React.Dispatch<boolean>;
    refresh: () => void;
    receiver?: SelectedReceiver;
    kid?: string;
}

const CreateAvtaleGiro: React.FC<Props> = ({ show, setShow, refresh, receiver, kid }) => {
    const translated = useTranslation();

    const { signingStatus, signingError, signingOperationPending, dispatch } = useSigningContext();
    const { handleChange } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();

    const account = usePaymentAccount(isValidAvtalegiroFromAccount);
    const refreshAccounts = useAccountChange();
    const refreshAvtaleGiroProposals = useRefreshAvtaleGiroProposals();

    const updateOnChange = useCallback(() => {
        const hasChanged = !!signingError || !!signingStatus || signingOperationPending;
        if (hasChanged && targetFromAccount?.accountId) {
            refreshAccounts([targetFromAccount.accountId]);
            refresh();
            refreshAvtaleGiroProposals();
        }
    }, [
        signingError,
        signingStatus,
        signingOperationPending,
        targetFromAccount?.accountId,
        refreshAccounts,
        refresh,
        refreshAvtaleGiroProposals,
    ]);

    const onCloseAvtaleGiro = useCallback(() => {
        updateOnChange();
        handleChange({
            type: AvtaleGiroActionType.RESET,
        });
        dispatch({ type: Actions.RESET });
        setShow(false);
    }, [dispatch, handleChange, setShow, updateOnChange]);

    useEffect(() => {
        if (receiver) {
            handleChange({ type: AvtaleGiroActionType.TO_ACCOUNT_SELECTED, payload: receiver });
        }
    }, [handleChange, receiver]);

    useEffect(() => {
        if (kid) {
            handleChange({ type: AvtaleGiroActionType.SET_KID, payload: { value: kid } });
        }
    }, [handleChange, kid]);

    return (
        <EikaModal
            id="create-avtalegiro__modal"
            className="create-avtalegiro__modal"
            open={show}
            setOpen={setShow}
            ariaLabel={translated.avtaleGiroAgreement.createTitle}
            onClose={onCloseAvtaleGiro}
            timeOut={signingStatus === 'COMPLETED' ? 2500 : undefined}
            contentShouldStretch
            size="wide"
            desktopMinHeight="small"
        >
            <CreateAvtaleGiroForm onClose={onCloseAvtaleGiro} account={account ?? undefined} />
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.CREATE_AVTALEGIRO}>
        <AvtalegiroFormContext
            validateHook={useValidate}
            reducer={avtalegiroReducer}
            initialState={avtalegiroInitialState}
        >
            <CreateAvtaleGiro {...props} />
        </AvtalegiroFormContext>
    </SigningContextProvider>
);
